import { SimpleAlertModal } from '@boostbrothers/web-ui/web';
import { TextEditor } from '@boostbrothers/wysiwyg';
import useModal from '~hooks/useModal';
import useImageMutation from '../hooks/useImageMutation';
import FormThumbnail from './FormThumbnail';

interface FormProps {
  title: string;
  document: string;
  previewText: string;
  thumbnail: { url: string; fileName: string };
  fromPA: boolean;
  setTitle: (title: string) => void;
  setDocument: (document: string) => void;
  setPreviewText: (previewText: string) => void;
  setThumbnail: (thumbnail: { url: string; fileName: string }) => void;
  onCursorNodeChange?: (nodeIndex: number) => void;
  handleTemporarySave: () => void;
}

const Form = ({
  title,
  document,
  previewText,
  thumbnail,
  fromPA,
  setTitle,
  setDocument,
  setPreviewText,
  setThumbnail,
  onCursorNodeChange,
  handleTemporarySave,
}: FormProps) => {
  const { openModal, closeModal } = useModal();
  const { uploadImage: uploadEditorImage } = useImageMutation({
    handleTemporarySave,
    fromPA,
  });

  const handleImageUploadError = ({
    title,
    message,
  }: {
    title: string;
    message: string;
  }) => {
    openModal({
      modalContent: (
        <SimpleAlertModal
          title={title}
          description={message}
          confirmAction={() => closeModal()}
        />
      ),
    });
  };

  return (
    <div className="flex flex-1 flex-col gap-6">
      {/* Title */}
      <div className="flex shrink-0 flex-col gap-3">
        <div className="flex items-end justify-start gap-x-2">
          <span className="text-base text-CoolGray-900">타이틀</span>
          <span className="text-sm text-CoolGray-400">
            최대 25자까지 입력됩니다.
          </span>
        </div>
        <textarea
          maxLength={25}
          rows={2}
          value={title}
          onChange={e => setTitle(e.target.value)}
          onKeyDown={e => {
            // Enter 키를 눌렀을 때 이미 2줄이면 막기
            if (e.key === 'Enter' && title.split('\n').length >= 2) {
              e.preventDefault();
            }
          }}
          className="rounded-lg p-5 text-xl font-bold text-CoolGray-900 bg-CoolGray-50 focus:ring-0 outline-none border-none resize-none"
          placeholder="타이틀을 입력해주세요."
        />
      </div>

      {/* Preview Text */}
      <div className="flex shrink-0 flex-col gap-3">
        <div className="flex items-end justify-start gap-x-2">
          <span className="text-base text-CoolGray-900">머리글</span>
          <span className="text-sm text-CoolGray-400">
            미작성 시, 본문 내용이 자동으로 노출됩니다.
          </span>
        </div>
        <textarea
          rows={3}
          value={previewText}
          onChange={e => setPreviewText(e.target.value)}
          className="rounded-lg p-5 text-base leading-6 text-CoolGray-900 bg-CoolGray-50 focus:ring-0 outline-none border-none resize-none"
          placeholder="머리글을 입력해주세요."
        />
      </div>

      {/* Content Editor */}
      <div className="flex h-full flex-1 flex-col">
        <label htmlFor="content" className="text-base text-CoolGray-900">
          본문
        </label>
        <TextEditor
          value={document}
          autoFocus={false}
          placeholder="본문 내용을 입력해주세요."
          dropdownUnits={['h1', 'h2', 'p', 'sub']}
          toolbarUnits={[
            'blockType',
            'bold',
            'strike',
            'underline',
            'code',
            'image',
            'divider',
            'link',
          ]}
          deeplinkBaseUrl={
            process.env.BUILD_ENV === 'test' ? 'sayuptest://' : 'sayup://'
          }
          onChange={setDocument}
          onCursorNodeChange={onCursorNodeChange}
          onImageUpload={file => uploadEditorImage(file)}
          onImageUploadError={error => handleImageUploadError(error)}
        />
      </div>

      {/* Thumbnail */}
      <FormThumbnail
        fromPA={fromPA}
        thumbnail={thumbnail}
        setThumbnail={setThumbnail}
        handleTemporarySave={handleTemporarySave}
      />
    </div>
  );
};

export default Form;
